import { useState } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = `${window.location.origin}/api`;

const QUESTIONS = [
  {
    key: "q1",
    label: "What old mindsets are being challenged in this season?",
    hint: "Are there beliefs you need to release to embrace the new?",
  },
  {
    key: "q2",
    label: "What has the Lord been speaking to you repeatedly\u2014but you haven\u2019t fully acted on yet?",
    hint: "This could be a topic, a step of action, behavior or mindset.",
  },
  {
    key: "q3",
    label: "Have you given God full access\u2014or just selective access?",
    hint: "What areas are still off-limits or tightly controlled?",
  },
  {
    key: "q4",
    label: "What is your greatest blockage in this season?",
    hint: "Think emotional, physical, and spiritual.",
  },
  {
    key: "q5",
    label: "Give us 3 topics you want to hear more about.",
    hint: "OR: What is the Lord saying to you prophetically in this season that you need revelation on to fully understand?",
  },
];

export default function AllAccessPollPage() {
  const [form, setForm] = useState({ name: "", email: "", q1: "", q2: "", q3: "", q4: "", q5: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/all-access-poll`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.status === 429) {
        toast.error("Too many submissions. Please try again later.");
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Submission failed");
      }
      setSubmitted(true);
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="all-access-thank-you">
          <div className="max-w-md text-center">
            <div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-5">
              <svg className="w-7 h-7 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="font-heading text-2xl text-navy mb-3">Thank you for filling this out.</h2>
            <p className="text-gray-500 text-sm leading-relaxed">Your responses have been submitted to our leadership team.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-navy py-12 sm:py-16 text-center" data-testid="all-access-hero">
        <div className="border-b-2 border-gold w-12 mx-auto mb-5" />
        <h1 className="font-heading text-3xl sm:text-4xl text-bone">All Access Mentorship</h1>
        <p className="text-gray-300 mt-3 max-w-lg mx-auto px-4 text-sm leading-relaxed">
          This form is YOUR opportunity to voice what's important to you, to give personal perspective off screen, and receive prophetic coaching in an upcoming session.
        </p>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-10">
        <form onSubmit={handleSubmit} className="space-y-8" data-testid="all-access-form">
          {/* Name & Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="aa-name" className="text-navy font-medium">Full Name <span className="text-red-500">*</span></Label>
              <Input
                id="aa-name"
                data-testid="all-access-name"
                placeholder="Your full name"
                value={form.name}
                onChange={e => set("name", e.target.value)}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="aa-email" className="text-navy font-medium">Email <span className="text-red-500">*</span></Label>
              <Input
                id="aa-email"
                data-testid="all-access-email"
                type="email"
                placeholder="your@email.com"
                value={form.email}
                onChange={e => set("email", e.target.value)}
                required
                className="mt-1.5"
              />
            </div>
          </div>

          {/* 5 Questions */}
          {QUESTIONS.map((q, i) => (
            <div key={q.key}>
              <Label className="text-navy font-medium leading-snug block mb-1.5">
                {i + 1}. {q.label}
              </Label>
              <p className="text-xs text-gray-400 mb-2 italic">{q.hint}</p>
              <Textarea
                data-testid={`all-access-${q.key}`}
                rows={4}
                value={form[q.key]}
                onChange={e => set(q.key, e.target.value)}
                placeholder="Share your thoughts..."
              />
            </div>
          ))}

          <Button
            type="submit"
            data-testid="all-access-submit"
            disabled={submitting}
            className="w-full bg-gold hover:bg-gold/90 text-navy font-heading text-base py-5"
          >
            {submitting ? "Submitting..." : "Submit"}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
